/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  getPoles,
  setPoleAlarms,
  setPoleLedRBG,
  setPoleLedSchedule,
  setPoleLedWhite,
} from './_requests';
import { deepMerge } from './_functions';
import { LedSchedule, Firmware, Pole, LedRGB, LedWhite } from './_models';
import { socket } from '../socket';
import { useAuth } from '../auth/AuthContext';
import { useEquipmentContext } from '../equipment/EquipmentContext';
import {
  getSelectedPole,
  removeSelectedPole,
  setSelectedPole,
} from './PolesHelpers';

interface PolesContextProps {
  isLoading: boolean;
  currentPole?: Pole;
  poles: Pole[];
  setPoleId: (id?: number) => void;
  onChangeRGB: (config: LedRGB) => void;
  onChangeWhite: (config: LedWhite) => void;
  onChangeLedProfile: (config: LedSchedule) => void;
  onChangeAlarms: (active: boolean) => void;
}

const PolesContext = createContext<PolesContextProps | undefined>(undefined);

export const usePolesContext = () => {
  const context = useContext(PolesContext);
  if (!context) {
    throw new Error('usePolesContext must be used within a PoleProvider');
  }
  return context;
};

export function PoleProvider({ children }: { children: React.ReactNode }) {
  const { currentUser } = useAuth();
  const { equipment } = useEquipmentContext();

  const [poles, setPoles] = useState<Pole[]>([]);
  const [poleId, setPoleId] = useState<number | undefined>(getSelectedPole());
  const [isLoading, setIsLoading] = useState(true);

  const [loadingRGB, setLoadingRGB] = useState(false);
  const [loadingWhite, setLoadingWhite] = useState(false);
  const [loadingLedProfile, setLoadingLedProfile] = useState(false);
  const [loadingAlarms, setLoadingAlarms] = useState(false);

  const currentPole = useMemo<Pole | undefined>(
    () => (poles ? poles.find((p) => p.id === poleId) : undefined),
    [poleId, poles]
  );

  useEffect(() => {
    if (!poleId || isNaN(poleId)) removeSelectedPole();
    else setSelectedPole(poleId);
  }, [poleId]);

  const onFirmware = ({ firmware }: { firmware: Firmware }) => {
    console.debug('firmware', firmware);

    if (!currentPole || !firmware) return;

    setPoles((prev) =>
      prev.map((p) => {
        if (p.id !== currentPole.id) return p;
        return {
          ...p,
          firmware: deepMerge({}, p.firmware, firmware) as Firmware,
        };
      })
    );
  };

  useEffect(() => {
    const serial = currentPole?.serial;

    if (serial) {
      socket.emit('join', serial);
      console.debug('join', serial);

      socket.on('firmware', onFirmware);
    }

    return () => {
      socket.off('firmware', onFirmware);

      if (serial) {
        socket.emit('leave', serial);
        console.debug('leave', serial);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPole?.serial]);

  useEffect(() => {
    setIsLoading(true);
    if (!currentUser) {
      setPoles([]);
      setPoleId(undefined);
      setIsLoading(false);
    } else {
      getPoles()
        .then(({ data }) => setPoles(data))
        .finally(() => setIsLoading(false));
    }
  }, [currentUser]);

  const onChangeRGB = (config: LedRGB) => {
    if (!poleId || loadingRGB) return;

    setLoadingRGB(true);

    setPoleLedRBG(poleId, config).then((data) => {
      console.debug('onChangeRGB', config, data);

      setPoles((prev) =>
        prev.map((p) => {
          if (p.id !== poleId) return p;

          return {
            ...p,
            firmware: {
              ...p.firmware,
              ledrgb: { ...p.firmware.ledrgb, ...config },
            },
          };
        })
      );

      setTimeout(() => setLoadingRGB(false), 500);
    });
  };

  const onChangeWhite = (config: LedWhite) => {
    if (!poleId || loadingWhite) return;

    setLoadingWhite(true);

    setPoleLedWhite(poleId, config).then((data) => {
      console.debug('setLedW', config, data);

      setPoles((prev) =>
        prev.map((p) => {
          if (p.id !== poleId) return p;

          return {
            ...p,
            firmware: {
              ...p.firmware,
              ledw: { ...p.firmware.ledw, ...config },
            },
          };
        })
      );

      setTimeout(() => setLoadingWhite(false), 500);
    });
  };

  const onChangeLedProfile = (config: LedSchedule) => {
    if (!poleId || loadingLedProfile) return;

    setLoadingLedProfile(true);

    setPoleLedSchedule(poleId, config).then((data) => {
      console.debug('onChangeLedProfile', config, data);

      setPoles((prev) =>
        prev.map((p) => {
          if (p.id !== poleId) return p;

          return {
            ...p,
            firmware: {
              ...p.firmware,
              ledschedule: config,
            },
          };
        })
      );

      setTimeout(() => setLoadingLedProfile(false), 500);
    });
  };

  const onChangeAlarms = (active: boolean) => {
    if (!poleId || loadingAlarms) return;

    setLoadingAlarms(true);

    setPoleAlarms(poleId, active).then((data) => {
      console.debug('setPoleAlarms', data);

      const alarms = equipment.filter((eq) => eq.type === 'alarms');

      setPoles((prev) =>
        prev.map((p: Pole) => {
          if (p.id !== poleId) return p;

          const keys = Object.keys(p.firmware).filter((k) =>
            k.startsWith('in')
          ) as (keyof Firmware)[];

          const firmware = keys.reduce((acc, e) => {
            if (
              alarms.some(
                (a) => 'name' in p.firmware[e] && a.name === p.firmware[e].name
              )
            )
              return {
                ...acc,
                [e]: { ...p.firmware[e], alert: active },
              };

            return acc;
          }, p.firmware);

          return { ...p, firmware };
        })
      );

      setTimeout(() => setLoadingAlarms(false), 500);
    });
  };

  return (
    <PolesContext.Provider
      value={{
        isLoading,
        currentPole,
        poles,
        setPoleId,
        onChangeRGB,
        onChangeWhite,
        onChangeLedProfile,
        onChangeAlarms,
      }}
    >
      {children}
    </PolesContext.Provider>
  );
}
